import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconClose } from '@components/icons';
import { IconButton } from '@components/IconButton';
import { Button } from '@components/Button';
import styles from './DeleteLeadModal.module.scss';

export interface DeleteLeadModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Lead name to display in confirmation message */
  leadName: string;
  /** Called when user confirms deletion. Returns a promise for loading state. */
  onConfirm: () => Promise<void> | void;
  /** Custom modal title (defaults to t('leads.deleteLead')) */
  title?: string;
  /** Custom confirmation message (defaults to t('deleteLead.confirmMessage')) */
  confirmMessage?: string;
}

const DeleteLeadForm = ({
  onClose,
  leadName,
  onConfirm,
  title,
  confirmMessage,
}: Pick<DeleteLeadModalProps, 'onClose' | 'leadName' | 'onConfirm' | 'title' | 'confirmMessage'>) => {
  const { t } = useTranslation();
  const [stableLeadName] = useState(leadName);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
      role="presentation"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog needs event handlers */}
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-lead-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header}>
          <h2 id="delete-lead-title" className={styles.title}>
            {title || t('leads.deleteLead')}
          </h2>
          <IconButton
            size="small"
            aria-label={t('deleteLead.closeModal')}
            onClick={onClose}
            className={styles.closeButton}
          >
            <IconClose size={18} />
          </IconButton>
        </div>

        {/* Content */}
        <div className={styles.content}>
          <p className={styles.message}>
            {confirmMessage || t('deleteLead.confirmMessage', { leadName: stableLeadName })}
          </p>
          {error && <p className={styles.error}>{error}</p>}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button variant="primary" size="small" onClick={onClose} disabled={isDeleting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            size="small"
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? t('deleteLead.deleting') : t('common.delete')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const DeleteLeadModal = ({
  isOpen,
  onClose,
  leadName,
  onConfirm,
  title,
  confirmMessage,
}: DeleteLeadModalProps) => {
  if (!isOpen) return null;
  return (
    <DeleteLeadForm
      onClose={onClose}
      leadName={leadName}
      onConfirm={onConfirm}
      title={title}
      confirmMessage={confirmMessage}
    />
  );
};
